import { TabClickProps } from '@/components/atom/tab';
import { BottomSheetTabsListItemProps } from '@/components/molecules/bottom-sheet/bottom-sheet-tabs/bottom-sheet-tabs-list';
import useSetAuthOnScreenFocus from '@/hooks/auth/useSetAuthOnScreenFocus';
import useGetCommunityUser from '@/hooks/community/useGetCommunityUser';
import { useAddQuestionWithAiAnswerMutation } from '@/hooks/qna/mobile/qna.gql.impl';
import usePolicyAgreementBottomSheetViewModel, {
  PolicyAgreementBottomSheetViewModel,
} from '@/hooks/qna/mobile/write/usePolicyAgreementBottomSheetViewModel';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import useNeverSeeAgain from '@/hooks/useNeverSeeAgain';
import { tokenState } from '@/recoil/atoms/common/auth';
import { userState } from '@/recoil/atoms/common/user';
import { realTimeAnswerState } from '@/recoil/atoms/qna';
import { AiAnswerStatus, QuestionStatus } from '@/types/bff/gql-schema';
import { Nullable, ROUTES } from '@/types/common';
import { CommunityUser } from '@/types/community';
import { GraphqlError } from '@/types/error';
import { UserCoordinate } from '@/types/hospital';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { AddQuestionResponse, QnaCategoryGroup, QnaCategoryType } from '@/types/qna';
import { getBffErrorMessage } from '@/utils/error';
import { communityLogger, mobileLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { useToast } from 'doctornow-design-system';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { EPixelEventName } from '@/constants/pixel';
import Pixel from '@/utils/pixel/pixel';
import useGetMyUncheckedAiAnswer from './mobile/home/useGetMyUncheckedAiAnswer';
import useQnaWriteBottomSheetGroup, { UseQnaWriteBottomSheetGroup } from './mobile/write/useQnaWriteBottomSheetGroup';
import useGetQnaCategoryGroup from './useGetQnaCategoryGroup';

interface QnaWriteSubmitProps {
  coordinate?: UserCoordinate;
  isAiAnswer?: boolean;
}

export interface QnaWriteMobileViewModel {
  question: string;
  setQuestion: Dispatch<string>;
  onClickCheckPregnant: () => void;
  pregnant: boolean;
  selectedCategoriesBottomSheetTabItem: Nullable<BottomSheetTabsListItemProps>;
  selectedTags: string[];
  setSelectedTags: Dispatch<SetStateAction<string[]>>;
  showCategoriesBottomSheet: boolean;
  toggleCategoriesBottomSheet: (value: boolean) => void;
  isSubmitLoading: boolean;
  userProfile?: CommunityUser;
  onClickCategoriesBottomSheetTabListItem: (item: BottomSheetTabsListItemProps) => void;
  categoryGroup?: QnaCategoryGroup;
  selectedTab: QnaCategoryType;
  onClickCategoriesBottomSheetTab: (tab: TabClickProps<QnaCategoryType>) => void;
  getSelectedTabItemList: () => BottomSheetTabsListItemProps[];
  onClickNearDoctorAlertSkip: () => void;
  checkEnableSubmit: (e: React.MouseEvent) => void;
  isSubmitButtonDisabled: boolean;
  qnaWriteBottomSheetGroup: UseQnaWriteBottomSheetGroup;
  requestAiAnswer: () => void;
  policyAgreementBottomSheetViewModel: PolicyAgreementBottomSheetViewModel;
}

const useQnaWriteMobileViewModel = (): QnaWriteMobileViewModel => {
  useSetAuthOnScreenFocus();
  useNeverSeeAgain();
  const { setShowAuthModal } = useAuthModal();
  const token = useRecoilValue(tokenState);
  const { pageName, moveToPage } = useCustomRouter();
  const { toast } = useToast();
  const { showModal, closeModal } = useModal();
  const setRealTimeAnswerState = useSetRecoilState(realTimeAnswerState);
  const qnaWriteBottomSheetGroup = useQnaWriteBottomSheetGroup();
  const unCheckedAiAnswer = useGetMyUncheckedAiAnswer();
  const policyAgreementBottomSheetViewModel = usePolicyAgreementBottomSheetViewModel();
  const [question, setQuestion] = useState<string>('');
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [pregnant, setPregnant] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Array<string>>([]);
  const [showCategoriesBottomSheet, setShowCategoriesBottomSheet] = useState<boolean>(false);
  const { userProfileData } = useGetCommunityUser();
  const user = useRecoilValue(userState);

  const [selectedTab, setSelectedTab] = useState<QnaCategoryType>(QnaCategoryType.TREATMENT);

  const { categoryGroup } = useGetQnaCategoryGroup();

  // 질문 작성
  const { mutate: addQuestionWithAiAnswerMutate } = useAddQuestionWithAiAnswerMutation();

  const [selectedCategoriesBottomSheetTabItem, setSelectedCategoriesBottomSheetTabItem] =
    useState<Nullable<BottomSheetTabsListItemProps>>();

  const isSubmitButtonDisabled = useMemo(() => {
    return isSubmitLoading || (userProfileData?.isBanned ?? false);
  }, [isSubmitLoading, userProfileData?.isBanned]);

  const onSubmitSuccess = (response: AddQuestionResponse) => {
    if (!isWebView()) {
      setRealTimeAnswerState(true);
    }

    mobileLogger({
      logger: {
        eventName: 'quick_answer_write_submit_click',
        pageName: pageName,
      },
    });

    const {
      addQuestionV2: { aiAnswerAvailable, questionPid },
    } = response;
    if (aiAnswerAvailable) {
      moveToPage({
        webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
        mobileScreenName: ROUTES.QNA.AI.appScreenName,
        params: { questionPid: questionPid },
        type: NATIVE_POST_MESSAGE_TYPE.REPLACE_TO_PAGE,
      });

      return;
    }

    moveToPage({
      webLink: `${ROUTES.QNA.AITITLE.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AITITLE.appScreenName,
      params: { questionPid: questionPid },
      type: NATIVE_POST_MESSAGE_TYPE.REPLACE_TO_PAGE,
    });
  };

  const onSubmitError = (error: GraphqlError) => {
    return toast({
      text: getBffErrorMessage(error),
      direction: 'bottom',
    });
  };

  const onClickCheckPregnant = () => {
    setPregnant((prev) => !prev);
  };

  // 카테고리 Bottom Sheet Show/Hide Toggle
  const toggleCategoriesBottomSheet = (value = true) => {
    setShowCategoriesBottomSheet(value);
  };

  // 카테고리 Bottom Sheet Tab Click
  const onClickCategoriesBottomSheetTab = (tab: TabClickProps<QnaCategoryType>) => {
    setSelectedTab(tab.value);
  };

  const getSelectedTabItemList = () => {
    if (!categoryGroup) return [] as BottomSheetTabsListItemProps[];
    return categoryGroup.tabsList.filter((tab) => tab.key === selectedTab)[0].categories;
  };

  // Bottom Sheet Tab List Item Click Handler
  const onClickCategoriesBottomSheetTabListItem = (item: BottomSheetTabsListItemProps) => {
    setSelectedCategoriesBottomSheetTabItem(item);
    toggleCategoriesBottomSheet(false);
  };

  const checkEnableSubmit = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!token) {
      setShowAuthModal(true);
      return;
    }

    if (isWebView()) {
      if (!user.isCertified) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_CERTIFICATION_SCREEN, {
          params: {
            base: 'qna',
          },
        });
        return false;
      }
    }

    if (question.length <= 4) {
      toast({ text: '질문을 최소 5자 이상 입력해주세요.', direction: 'bottom' });
      return false;
    }
    if (!selectedCategoriesBottomSheetTabItem) {
      toast({ text: '카테고리를 선택해주세요.', direction: 'bottom' });
      return false;
    }

    if (
      policyAgreementBottomSheetViewModel.hasAlreadyAgreedToAll() ||
      policyAgreementBottomSheetViewModel.hasAgreedToMinimum()
    ) {
      requestAiAnswer();
    } else {
      policyAgreementBottomSheetViewModel.openBottomSheet();
    }
  };

  const requestAiAnswer = () => {
    communityLogger({
      eventName: 'click_ai_answer_bottom_sheet',
      pageName: pageName,
      payload: {
        type: qnaWriteBottomSheetGroup.activeBottomSheetType ?? '',
      },
    });

    onSubmit({ isAiAnswer: true });
  };

  // 질문 submit
  const onSubmit = async ({ coordinate, isAiAnswer }: QnaWriteSubmitProps) => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {});

    if (!selectedCategoriesBottomSheetTabItem) return;
    setIsSubmitLoading(true);
    addQuestionWithAiAnswerMutate(
      {
        request: {
          content: question,
          pregnant,
          categoryId: selectedCategoriesBottomSheetTabItem.value as string,
          tagNames: selectedTags,
          aiAnswerAvailable: isAiAnswer,
          location: coordinate ?? null,
        },
      },
      {
        onSuccess: onSubmitSuccess,
        onError: onSubmitError,
      }
    );
  };

  const onClickNearDoctorAlertSkip = () => {
    communityLogger({
      eventName: 'click_near_doctor_alert_skip',
      pageName: pageName,
      payload: {
        type: qnaWriteBottomSheetGroup.activeBottomSheetType ?? '',
      },
    });
    qnaWriteBottomSheetGroup.onChangeBottomSheetType();
    onSubmit({
      isAiAnswer: selectedCategoriesBottomSheetTabItem?.aiAnswerAvailable,
    });
  };

  const onClickAiAnswerDetail = (questionPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AI.appScreenName,
      params: {
        questionPid,
      },
    });
  };

  useEffect(() => {
    setSelectedTags([]);
  }, [selectedCategoriesBottomSheetTabItem]);

  useEffect(() => {
    if (isWebView()) {
      if (question || selectedCategoriesBottomSheetTabItem) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.SET_PREVENT_BACK, {
          preventBack: true,
        });
      } else {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.SET_PREVENT_BACK, {
          preventBack: false,
        });
      }
    }
  }, [question, selectedCategoriesBottomSheetTabItem]);

  useEffect(() => {
    if (userProfileData?.isBanned) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '서비스 이용 제한',
          message: '실시간 상담 운영 정책 위반으로 활동이 제한 되었습니다.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '확인',
              onClick: () => {
                closeModal();
                historyBack();
              },
            },
          ],
        },
      });
    }
  }, [userProfileData]);

  useEffect(() => {
    if (
      unCheckedAiAnswer &&
      (unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationRequested ||
        unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.Generating ||
        unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationComplete ||
        unCheckedAiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationFailed) &&
      unCheckedAiAnswer.status !== QuestionStatus.PaymentFailed
    )
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '기존 답변을 먼저 확인해 주세요',
          message: '고민이 해결됐는지 의료진 상담이 필요한지 알려주신 뒤 다음 질문을 작성할 수 있어요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '확인',
              onClick: () => {
                closeModal();
                historyBack();
              },
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '답변 보기',
              onClick: () => {
                closeModal();
                onClickAiAnswerDetail(unCheckedAiAnswer.questionPid);
              },
            },
          ],
        },
      });
  }, [unCheckedAiAnswer?.aiAnswerStatus]);

  useEffect(() => {
    Pixel.trackCustom(EPixelEventName.PageIn, {});
  }, []);

  return {
    question,
    setQuestion,
    onClickCheckPregnant,
    pregnant,
    selectedCategoriesBottomSheetTabItem,
    selectedTags,
    setSelectedTags,
    showCategoriesBottomSheet,
    toggleCategoriesBottomSheet,
    isSubmitLoading,
    userProfile: userProfileData,
    onClickCategoriesBottomSheetTabListItem,
    categoryGroup,
    selectedTab,
    onClickCategoriesBottomSheetTab,
    getSelectedTabItemList,
    onClickNearDoctorAlertSkip,
    checkEnableSubmit,
    isSubmitButtonDisabled,
    qnaWriteBottomSheetGroup,
    requestAiAnswer,
    policyAgreementBottomSheetViewModel: policyAgreementBottomSheetViewModel,
  };
};
export default useQnaWriteMobileViewModel;
